import React, { useMemo } from 'react' // useMemo used in tooltip memoization
import { StakingBadge } from '@components/StakingBadge/StakingBadge'
import { useStakingBadge } from '@components/StakingBadge/useStakingBadges'
import { useEthPrice } from '@components/Web3/useEthPrice'
import { Stack, Text } from '@ui'
import { useCombinedEthAndUSDCInUSD } from 'hooks/useFormattedNormalizedTipAmount'
import { useOtherUsersAbstractAccountAddress } from 'hooks/useOtherUsersAbstractAccountAddress'
import { useOptimisticTips } from 'hooks/useTipsReceivedCount'
import { useTotalStakedByUser } from 'use-towns-client'

export const TipCounter = ({ userId }: { userId?: string }) => {
    const { ethAmount, usdcAmount } = useOptimisticTips(userId)

    const totalAmount = ethAmount + usdcAmount

    const shouldShowAmount = totalAmount > 0n

    // Refetch ETH price every 30 seconds to keep USD conversion current
    const { data: ethPrice } = useEthPrice({
        refetchInterval: 30_000,
    })

    const tipsReceivedAmountInUsd = useCombinedEthAndUSDCInUSD(ethAmount, usdcAmount, ethPrice, {
        noDecimals: true,
    })

    const { data: otherUsersAbstractAccountAddress } = useOtherUsersAbstractAccountAddress(userId)
    const { data, isLoading } = useTotalStakedByUser({
        userAddress: otherUsersAbstractAccountAddress,
    })

    const totalStaked = data?.totalStaked ? Number(BigInt(data.totalStaked) / 10n ** 18n) : 0
    const stakingBadge = useStakingBadge(totalStaked)

    const tooltip = useMemo(() => {
        const tipsString = shouldShowAmount
            ? `${tipsReceivedAmountInUsd} in tips received`
            : undefined
        if (totalStaked > 0) {
            return `${stakingBadge.staker} ${tipsString ? `• ${tipsString}` : ''}`
        }
        return tipsString
    }, [shouldShowAmount, tipsReceivedAmountInUsd, totalStaked, stakingBadge.staker])

    if (!userId || (!shouldShowAmount && totalStaked === 0)) {
        return null
    }

    return (
        <Stack horizontal gap="xs" alignItems="center" tooltip={tooltip}>
            <StakingBadge
                type={isLoading && !stakingBadge.type ? 'loading' : stakingBadge.type}
                size="x2"
            />
            {shouldShowAmount && (
                <Text fontSize="sm" color="gray2" as="span">
                    {tipsReceivedAmountInUsd.replace('$', '')}
                </Text>
            )}
        </Stack>
    )
}
